import * as React from 'react';
import { Input, InputProps } from '@/components/ui-kit/input';

/** "HH:mm" of what was typed so far: digits only, the colon added after the hours. */
const maskTime = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
};

/** A short entry completed on blur: "8" → "08:00", "17" → "17:00", "830" → "08:30". */
const completeTime = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 1 || digits.length === 2) return `${digits.padStart(2, '0')}:00`;
  if (digits.length === 3) return `0${digits[0]}:${digits.slice(1)}`;
  return value;
};

export interface TimeInputProps extends Omit<InputProps, 'type' | 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
}

/**
 * A 24-hour "HH:mm" time field typed as text, the same on every browser and locale (a native
 * `type="time"` shows AM/PM on 12-hour systems and is awkward to type into). Validate the value
 * with the form's schema; this only shapes what is typed.
 */
export const TimeInput = React.forwardRef<HTMLInputElement, TimeInputProps>(
  ({ value, onChange, onBlur, ...props }, ref) => (
    <Input
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder="00:00"
      maxLength={5}
      value={value ?? ''}
      onChange={(event) => onChange(maskTime(event.target.value))}
      onBlur={(event) => {
        const completed = completeTime(value ?? '');
        if (completed !== value) onChange(completed);
        onBlur?.(event);
      }}
      {...props}
    />
  )
);
TimeInput.displayName = 'TimeInput';
